import axios, { AxiosError } from 'axios';

import { datadogRum } from 'root/datadog/datadogRum';

const API_ROOT = '/admin/invoicing/v1/payment-terms';

const GET_DEFAULT_PAYMENT_TERM_ENDPOINT = `${API_ROOT}/default`;
const CREATE_DEFAULT_PAYMENT_TERM_ENDPOINT = API_ROOT;

export enum PaymentTermType {
  DueInXDays = 'dueInXDays',
  DueOnDayX = 'dueOnDayX',
  DueUponReceipt = 'dueUponReceipt',
}

export type SaveDefaultPaymentTermSettingsPayload =
  | {
      type: PaymentTermType.DueInXDays;
      numberOfDays: number;
    }
  | {
      type: PaymentTermType.DueOnDayX;
      schedule: {
        dayOfTheMonth: number;
        bufferInDays: number;
      };
    }
  | {
      type: PaymentTermType.DueUponReceipt;
    };

export interface DefaultPaymentTermSettings {
  id: string;
  type: PaymentTermType;
  /** Only present when type === DueInXDays. */
  numberOfDays?: number | null;
  /** Only present when type === DueOnDayX. */
  schedule: null | {
    dayOfTheMonth: number;
    bufferInDays: number;
  };
  /** The calculated due date for the current default term (ISO 8601 string). */
  dueDate: string;
  updatedAt: string;
  isDefault: boolean;
  createdByUserId: string;
  updatedByUserId: string;
}

export default class PaymentTermsService {
  /**
   * Fetches the default payment term settings.
   * @returns The settings when a default is configured (200), or null when none has been set (404).
   */
  static async getDefaultPaymentTermSettings(): Promise<DefaultPaymentTermSettings | null> {
    try {
      const response = await axios.get<DefaultPaymentTermSettings>(
        GET_DEFAULT_PAYMENT_TERM_ENDPOINT
      );
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<{ code?: string; message?: string }>;
      if (axiosError.response?.status === 404) {
        return null;
      }
      datadogRum.addError(error, {
        message: `LS_INVOICING_ERROR: Failed to fetch default payment term settings for account (${window.merchantos.account.id})`,
      });
      throw error;
    }
  }

  /**
   * Creates or updates the default payment term settings.
   *
   * - If no default exists, uses POST `/admin/invoicing/v1/payment-terms` and expects 201.
   * - If a default already exists, uses PUT `/admin/invoicing/v1/payment-terms` and expects 200.
   *
   * In both cases, the resolved value is the `DefaultPaymentTermSettings` object.
   */
  static async saveDefaultPaymentTermSettings(
    payload: SaveDefaultPaymentTermSettingsPayload
  ): Promise<DefaultPaymentTermSettings> {
    try {
      const existingDefault = await PaymentTermsService.getDefaultPaymentTermSettings();

      // No default exists yet -> create (POST, expect 201 Created).
      if (!existingDefault) {
        const response = await axios.post<DefaultPaymentTermSettings>(
          CREATE_DEFAULT_PAYMENT_TERM_ENDPOINT,
          payload
        );
        return response.data;
      }

      // Default exists -> update (PUT, expect 200 OK) using the default's id.
      return await PaymentTermsService.updateDefaultPaymentTermSettings(
        existingDefault.id,
        payload
      );
    } catch (error) {
      const axiosError = error as AxiosError<{ code?: string; message?: string }>;
      let finalError: unknown = error;
      let message = `LS_INVOICING_ERROR: Failed to save default payment term settings for account (${window.merchantos.account.id})`;

      // If the backend indicates that a default already exists when using POST,
      // fall back to a PUT (handles race conditions between reads/writes).
      if (
        axiosError.response?.status === 400 &&
        axiosError.response.data?.code === 'DEFAULT_PAYMENT_TERM_ALREADY_EXISTS'
      ) {
        try {
          const existingDefault = await PaymentTermsService.getDefaultPaymentTermSettings();

          if (!existingDefault) {
            throw error;
          }

          return await PaymentTermsService.updateDefaultPaymentTermSettings(
            existingDefault.id,
            payload
          );
        } catch (retryError) {
          finalError = retryError;
          message = `LS_INVOICING_ERROR: Failed to update default payment term settings after POST conflict for account (${window.merchantos.account.id})`;
        }
      }

      datadogRum.addError(finalError, {
        message,
      });
      throw finalError;
    }
  }

  private static async updateDefaultPaymentTermSettings(
    paymentTermId: string,
    payload: SaveDefaultPaymentTermSettingsPayload
  ): Promise<DefaultPaymentTermSettings> {
    const response = await axios.put<DefaultPaymentTermSettings>(
      `${API_ROOT}/${paymentTermId}`,
      payload
    );

    return response.data;
  }
}
